import { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, orderBy, query, where, limit as fsLimit } from 'firebase/firestore';
import { db } from '../../../firebase';
import { collections } from '../../../utils/firestore';

/**
 * Conditions OWNED by the viewer (P2-14): assigned to them (`ownerUid`) and, for a Department
 * Head, owned by their department (`ownerDeptId`) — these can live in another NWT/department
 * than where they were observed, so the persona-scoped `useTRLs` alone would miss them.
 * Also the conditions the viewer SUBMITTED (`submittedBy`) — Tim 20 Oct: something you logged
 * for another team five minutes ago must still show under "Mine" on your own dashboard.
 * One listener per active query; merged + de-duplicated with whatever the caller already has.
 */
export function useOwnedTRLs(viewer, { limit = 100 } = {}) {
  const [byUid, setByUid] = useState([]);
  const [byDept, setByDept] = useState([]);
  const [bySubmitter, setBySubmitter] = useState([]);
  const uid = viewer?.uid || null;
  const deptId = viewer?.role === 'department_lead' ? viewer?.deptId || null : null;

  useEffect(() => {
    if (!uid) {
      setByUid([]);
      return undefined;
    }
    const q = query(collection(db, collections.trls), where('ownerUid', '==', uid), orderBy('submittedAt', 'desc'), fsLimit(limit));
    return onSnapshot(q, (snap) => setByUid(snap.docs.map((d) => ({ id: d.id, ...d.data() }))), (err) => console.warn('useOwnedTRLs(uid):', err.message));
  }, [uid, limit]);

  useEffect(() => {
    if (!uid) {
      setBySubmitter([]);
      return undefined;
    }
    const q = query(collection(db, collections.trls), where('submittedBy', '==', uid), orderBy('submittedAt', 'desc'), fsLimit(limit));
    return onSnapshot(q, (snap) => setBySubmitter(snap.docs.map((d) => ({ id: d.id, ...d.data() }))), (err) => console.warn('useOwnedTRLs(submitter):', err.message));
  }, [uid, limit]);

  useEffect(() => {
    if (!deptId) {
      setByDept([]);
      return undefined;
    }
    const q = query(collection(db, collections.trls), where('ownerDeptId', '==', deptId), orderBy('submittedAt', 'desc'), fsLimit(limit));
    return onSnapshot(q, (snap) => setByDept(snap.docs.map((d) => ({ id: d.id, ...d.data() }))), (err) => console.warn('useOwnedTRLs(dept):', err.message));
  }, [deptId, limit]);

  return useMemo(() => [...byUid, ...byDept, ...bySubmitter], [byUid, byDept, bySubmitter]);
}

/** Merge lists of conditions by id, newest first. */
export function mergeConditions(...lists) {
  const map = new Map();
  for (const list of lists) for (const c of list || []) map.set(c.id, c);
  return [...map.values()].sort((a, b) => (b.submittedAt?.seconds || 0) - (a.submittedAt?.seconds || 0));
}

export default useOwnedTRLs;
